// SourceWizard — self-service source onboarding for admins as a guided,
// de-jargoned 3-step modal (Curro redesign):
//   1. CONNECT      — transport choice cards; upload an Excel/CSV dump (drop-
//                     zone) or point at an API (none / API-key / bearer / basic
//                     auth); optional daily auto-fetch schedule (UTC).
//   2. MAP COLUMNS  — pick the fact stream, probe the source to see its real
//                     columns + samples, then map them onto that stream's
//                     standard columns (auto-match does most of the typing).
//   3. TEST         — dry-run the full pipeline: nothing is recorded or
//                     published; you see rows, quality verdict and a sample of
//                     the final data.
// All SetupApi wiring is unchanged from the previous version.

import { useEffect, useRef, useState } from "react";
import { api } from "../hooks/useApi.js";
import Icon from "./Icon.jsx";
import { fmtRm, fmtN } from "../lib/format.js";

const SetupApi = {
  streams:   ()             => api.get("/api/admin/source-setup/streams"),
  canonical: (stream)       => api.get(`/api/admin/source-setup/canonical-columns?stream=${encodeURIComponent(stream)}`),
  upsert:    (body)         => api.post("/api/admin/source-setup", body),
  remove:    (key)          => fetch(`/api/admin/source-setup/${key}`, { method: "DELETE" }).then((r) => r.json()),
  probe:     (key, sheet)   => api.post(`/api/admin/source-setup/${key}/probe${sheet ? `?sheet=${encodeURIComponent(sheet)}` : ""}`),
  getProfile:(key)          => api.get(`/api/admin/source-setup/${key}/profile`),
  saveProfile:(key, p)      => fetch(`/api/admin/source-setup/${key}/profile`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(p) }).then(async (r) => { if (!r.ok) throw Object.assign(new Error(), { body: await r.json() }); return r.json(); }),
  test:      (key)          => api.post(`/api/admin/source-setup/${key}/test`),
  upload:    async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/admin/source-setup/upload", { method: "POST", body: fd, credentials: "same-origin" });
    if (!r.ok) throw Object.assign(new Error("upload failed"), { body: await r.json().catch(() => null) });
    return r.json();
  },
};

// Structural column auto-matcher. The canonical column names mirror the source
// headers (organisation, source_id, price_ex_vat → "ORGANISATION", "SOURCE",
// "PRICE EX-VAT"), so exact-normalised then substring matching is enough — no
// domain-specific synonym table is needed, and it works across all streams.
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
function suggest(canonical, sourceColumns) {
  const candidates = sourceColumns.map((c) => ({ c, n: norm(c) }));
  const target = norm(canonical);
  const exact = candidates.find((x) => x.n === target);
  if (exact) return exact.c;
  const part = candidates.find((x) => x.n.length > 2 && (x.n.includes(target) || target.includes(x.n)));
  return part ? part.c : "";
}

// Friendly label from a snake_case canonical name (price_ex_vat → "Price ex vat").
const titleize = (s) => s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
// Which canonical columns are dates (so we offer an optional strptime format).
const isDateLike = (name) => /date|dispatched|estimated|processed/.test(name);

const TRANSPORTS = [
  { key: "xlsx-drop", title: "Excel file", desc: "Upload an .xlsx workbook export", icon: "file" },
  { key: "csv-drop", title: "CSV file", desc: "Upload a comma / semicolon file", icon: "table" },
  { key: "http-api", title: "Live API", desc: "Pull from a URL on a schedule", icon: "api" },
];

function StepRing({ n, state, label, sub, icon }) {
  return (
    <div className={`sp ${state}`}>
      <div className="ring">{state === "done" ? <Icon name="check" size={16} /> : icon ? <Icon name={icon} size={15} /> : n}</div>
      <div className="lab"><b>{label}</b><span>{sub}</span></div>
    </div>
  );
}

export default function SourceWizard({ existing, onClose, onChanged }) {
  const isEdit = !!existing;
  const cfg0 = existing ? JSON.parse(existing.configJson || "{}") : {};
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const fileInput = useRef(null);
  const [drag, setDrag] = useState(false);

  // ---- 1. connection ----
  const [key, setKey] = useState(existing?.sourceKey ?? "");
  const [name, setName] = useState(existing?.displayName ?? "");
  const [transport, setTransport] = useState(existing?.transportKey ?? "xlsx-drop");
  const [path, setPath] = useState(cfg0.path ?? "");
  const [fileName, setFileName] = useState(cfg0.path ? cfg0.path.split(/[\\/]/).pop() : "");
  const [apiCfg, setApiCfg] = useState({
    url: cfg0.url ?? "", method: cfg0.method ?? "GET", format: cfg0.format ?? "",
    authType: cfg0.authType ?? "none", headerName: cfg0.headerName ?? "X-API-Key",
    apiKey: cfg0.apiKey ?? "", bearerToken: cfg0.bearerToken ?? "", username: cfg0.username ?? "", password: cfg0.password ?? "",
  });
  const [scheduleUtc, setScheduleUtc] = useState(existing?.scheduleUtc ?? "");
  const [uploading, setUploading] = useState(false);

  // ---- 2. schema ----
  const [streamsList, setStreamsList] = useState([]);
  const [stream, setStream] = useState("sales_invoice_line");   // which fact stream this source feeds
  const [canonical, setCanonical] = useState([]);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [sheet, setSheet] = useState(cfg0.sheet ?? "");
  const [mapping, setMapping] = useState({});       // canonical -> source column
  const [formats, setFormats] = useState({});       // canonical -> strptime format (date columns)
  const [schemaSaved, setSchemaSaved] = useState(false);
  const [justMatched, setJustMatched] = useState(false);

  // ---- 3. test ----
  const [testing, setTesting] = useState(false);
  const [testStage, setTestStage] = useState(0);
  const [test, setTest] = useState(null);

  useEffect(() => { SetupApi.streams().then(setStreamsList).catch(() => {}); }, []);
  useEffect(() => { if (stream) SetupApi.canonical(stream).then(setCanonical).catch(() => setCanonical([])); }, [stream]);
  useEffect(() => {
    if (!isEdit) return;
    SetupApi.getProfile(existing.sourceKey).then((p) => {
      const m = {}, f = {};
      for (const [canon, def] of Object.entries(p.columns ?? {})) { m[canon] = def.source; if (def.format) f[canon] = def.format; }
      setMapping(m);
      setFormats(f);
      if (p.stream) setStream(p.stream);
      setSheet(p.sheet ?? "");
      setSchemaSaved(true);
    }).catch(() => {});
  }, [isEdit, existing]);

  const probedCols = probe?.columns?.map((c) => c.name) ?? [];
  // keep already-mapped values selectable even before a probe (edit mode)
  const sourceCols = [...new Set([...probedCols, ...Object.values(mapping).filter(Boolean)])];

  const saveConnection = async () => {
    setError(null); setNotice(null);
    try {
      const config = transport === "http-api"
        ? Object.fromEntries(Object.entries(apiCfg).filter(([, v]) => v !== ""))
        : { path };
      if (transport !== "http-api" && !path) { setError("Upload a file (or enter a path) first."); return false; }
      if (transport === "http-api" && !apiCfg.url) { setError("Enter the API URL."); return false; }
      await SetupApi.upsert({ sourceKey: key, displayName: name || key, transportKey: transport, config, scheduleUtc: scheduleUtc || null });
      onChanged?.();
      return true;
    } catch (e) { setError(e.body?.error ?? e.message); return false; }
  };

  const doUpload = async (file) => {
    setUploading(true); setError(null);
    try {
      const r = await SetupApi.upload(file);
      setPath(r.path);
      setFileName(file.name);
      setNotice(`Uploaded ${file.name} (${(r.bytes / 1e6).toFixed(1)} MB).`);
    } catch (e) { setError(e.body?.error ?? "Upload failed."); }
    finally { setUploading(false); }
  };

  const doProbe = async (chosenSheet) => {
    setProbing(true); setError(null);
    try {
      const r = await SetupApi.probe(key, chosenSheet ?? sheet ?? undefined);
      setProbe(r);
      if (r.sheet) setSheet(r.sheet);
      setNotice(`Found ${r.columns.length} columns · ${Number(r.rowCount).toLocaleString()} rows. Map them below, or let auto-match do it.`);
    } catch (e) { setError(e.body?.error ?? e.message); }
    finally { setProbing(false); }
  };

  const autoMatch = () => {
    const m = { ...mapping };
    for (const c of canonical) if (!m[c]) m[c] = suggest(c, probedCols);
    setMapping(m);
    setJustMatched(true); setTimeout(() => setJustMatched(false), 900);
  };

  const saveSchema = async () => {
    setError(null); setNotice(null);
    try {
      const columns = {};
      for (const c of canonical) {
        columns[c] = { source: mapping[c] ?? "" };
        if (isDateLike(c) && formats[c]) columns[c].format = formats[c];
      }
      const reader = transport === "xlsx-drop" ? "xlsx" : transport === "http-api" ? (apiCfg.format === "json" ? "json" : "csv") : "csv";
      await SetupApi.saveProfile(key, { profileKey: key, version: 1, stream, reader, sheet: reader === "xlsx" ? sheet : null, columns });
      setSchemaSaved(true);
      return true;
    } catch (e) { setError(e.body?.error ?? e.message); return false; }
  };

  const runTest = async () => {
    setTesting(true); setError(null); setTest(null); setTestStage(0);
    const timers = [400, 1000, 1600, 2200].map((t, i) => setTimeout(() => setTestStage((s) => Math.max(s, i + 1)), t));
    try { setTest(await SetupApi.test(key)); }
    catch (e) { setError(e.body?.error ?? e.message); }
    finally { timers.forEach(clearTimeout); setTesting(false); }
  };

  const removeSource = async () => {
    if (!window.confirm(`Remove source '${existing.sourceKey}'? Its past snapshots stay available.`)) return;
    await SetupApi.remove(existing.sourceKey);
    onChanged?.(); onClose();
  };

  const mappedCount = canonical.filter((c) => mapping[c]).length;
  const allMapped = canonical.length > 0 && mappedCount === canonical.length;
  const canConnect = !!key && (transport === "http-api" ? !!apiCfg.url : !!path);

  const nextFromConnect = async () => {
    if (await saveConnection()) { setNotice(null); setStep(1); }
  };
  const nextFromMap = async () => {
    if (probe && allMapped) {
      if (!(await saveSchema())) return;
    }
    setNotice(null); setStep(2);
  };

  const steps = ["Connect", "Map columns", "Test"];
  const stepIcons = ["link", "wand", "flask"];
  const stepSubs = ["where & what", "what it means", "dry run"];

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="mh-ic"><Icon name={isEdit ? "settings" : "plus"} size={21} /></div>
          <div>
            <h2>{isEdit ? `Set up — ${existing.displayName}` : "Add a data source"}</h2>
            <div className="sub">{isEdit ? "Adjust the connection, mapping, or run a fresh test." : "Three quick steps. Nothing goes live until you process it."}</div>
          </div>
          <button type="button" className="x" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>

        {/* stepper */}
        <div className="stepper">
          {steps.map((s, i) => (
            <span key={s} style={{ display: "contents" }}>
              <StepRing n={i + 1} icon={stepIcons[i]} label={s} sub={stepSubs[i]}
                state={step === i ? "active" : step > i ? "done" : ""} />
              {i < steps.length - 1 && <div className={`line ${step > i ? "done" : ""}`}><i /></div>}
            </span>
          ))}
        </div>

        <div className="modal-body">
          {notice && <div className="notice"><Icon name="info" size={15} /> {notice}</div>}
          {error && <div className="error"><Icon name="alert" size={15} /> {error}</div>}

          {/* ---------- STEP 0 — CONNECT ---------- */}
          {step === 0 && (
            <div className="step-panel">
              <div className="wizard-q">Where does the data come from?</div>
              <p className="wizard-help">Pick how this source delivers its data. You can change it later.</p>
              <div className="choice-grid">
                {TRANSPORTS.map((t) => (
                  <button type="button" key={t.key} className={`choice ${transport === t.key ? "on" : ""}`} onClick={() => setTransport(t.key)}>
                    <div className="check"><Icon name="check" size={12} /></div>
                    <div className="ci"><Icon name={t.icon} size={19} /></div>
                    <b>{t.title}</b><small>{t.desc}</small>
                  </button>
                ))}
              </div>

              <div className="controls" style={{ marginTop: 20 }}>
                <div className="field"><label>Display name</label>
                  <input type="text" value={name} placeholder="e.g. Westfert monthly export"
                    onChange={(e) => { setName(e.target.value); if (!isEdit) setKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")); }} /></div>
                <div className="field"><label>Source id</label>
                  <input type="text" value={key} disabled={isEdit} placeholder="curro-monthly" onChange={(e) => setKey(e.target.value)} /></div>
                <div className="field"><label>Auto-fetch daily at (UTC) <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--muted-2)" }}>· optional</span></label>
                  <input type="text" value={scheduleUtc} placeholder="e.g. 03:00 — blank = manual only" onChange={(e) => setScheduleUtc(e.target.value)} /></div>
                <div className="field"><label>Fact stream</label>
                  <select value={stream} onChange={(e) => setStream(e.target.value)}>
                    {(streamsList.length ? streamsList : [stream]).map((s) => <option key={s} value={s}>{titleize(s)}</option>)}
                  </select></div>
              </div>
              <p className="hint" style={{ marginTop: 8 }}>The <b>fact stream</b> decides which standard columns this source maps to
                (sales invoices, orders, deliveries, weigh tickets, commissions, recipe inputs…).</p>
              {scheduleUtc && (
                <p className="hint" style={{ marginTop: 8, display: "flex", gap: 7, alignItems: "flex-start" }}>
                  <Icon name="clock" size={14} style={{ color: "var(--primary)", flex: "none", marginTop: 1 }} />
                  Every day at <b style={{ color: "var(--ink)" }}>&nbsp;{scheduleUtc} UTC&nbsp;</b> this source is fetched, normalised,
                  quality-checked and — if everything passes — auto-published. Failures appear in the history with a quality report.
                </p>
              )}

              {transport !== "http-api" ? (
                <div style={{ marginTop: 18 }}>
                  <label style={{ fontFamily: "var(--font-head)", fontWeight: 600, fontSize: 11, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--muted)" }}>Upload the file</label>
                  <input ref={fileInput} type="file" style={{ display: "none" }}
                    accept={transport === "xlsx-drop" ? ".xlsx,.xlsm" : ".csv,.tsv,.txt"}
                    onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])} />
                  <div className={`dropzone ${drag ? "drag" : ""}`} style={{ marginTop: 8 }}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.[0]) doUpload(e.dataTransfer.files[0]); }}
                    onClick={() => fileInput.current?.click()}>
                    <div className="dz-ic"><Icon name="upload" size={26} /></div>
                    <b>{uploading ? "Uploading…" : `Drop your ${transport === "xlsx-drop" ? "Excel" : "CSV"} file here`}</b>
                    <small>or click to browse · {transport === "xlsx-drop" ? ".xlsx, .xlsm" : ".csv, .tsv"}</small>
                    {fileName && !uploading && <div className="file"><Icon name="file" size={15} /> {fileName} <Icon name="check" size={14} style={{ color: "var(--pos)" }} /></div>}
                  </div>
                  <div className="controls" style={{ marginTop: 12 }}>
                    <div className="field" style={{ flex: 1 }}><label>…or a path on the server</label>
                      <input type="text" value={path} onChange={(e) => { setPath(e.target.value); setFileName(e.target.value.split(/[\\/]/).pop()); }} placeholder="appdata/uploads/…" style={{ width: "100%" }} /></div>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 18 }}>
                  <div className="controls">
                    <div className="field" style={{ flex: 2, minWidth: 280 }}><label>API URL</label>
                      <input type="text" value={apiCfg.url} placeholder="https://…/export" onChange={(e) => setApiCfg({ ...apiCfg, url: e.target.value })} style={{ width: "100%" }} /></div>
                    <div className="field"><label>Method</label>
                      <select value={apiCfg.method} onChange={(e) => setApiCfg({ ...apiCfg, method: e.target.value })}><option>GET</option><option>POST</option></select></div>
                    <div className="field"><label>Response format</label>
                      <select value={apiCfg.format} onChange={(e) => setApiCfg({ ...apiCfg, format: e.target.value })}>
                        <option value="">Auto-detect</option><option value="csv">CSV</option><option value="json">JSON</option>
                      </select></div>
                  </div>
                  <div className="controls" style={{ marginTop: 12 }}>
                    <div className="field"><label>Authentication</label>
                      <select value={apiCfg.authType} onChange={(e) => setApiCfg({ ...apiCfg, authType: e.target.value })}>
                        <option value="none">None</option><option value="apiKey">API key (header)</option>
                        <option value="bearer">Bearer token</option><option value="basic">Username + password</option>
                      </select></div>
                    {apiCfg.authType === "apiKey" && (
                      <>
                        <div className="field"><label>Header name</label><input type="text" value={apiCfg.headerName} onChange={(e) => setApiCfg({ ...apiCfg, headerName: e.target.value })} /></div>
                        <div className="field"><label>API key</label><input type="text" value={apiCfg.apiKey} placeholder="••••••••" onChange={(e) => setApiCfg({ ...apiCfg, apiKey: e.target.value })} /></div>
                      </>
                    )}
                    {apiCfg.authType === "bearer" && (
                      <div className="field" style={{ flex: 1 }}><label>Bearer token</label>
                        <input type="text" value={apiCfg.bearerToken} placeholder="••••••••" onChange={(e) => setApiCfg({ ...apiCfg, bearerToken: e.target.value })} style={{ width: "100%" }} /></div>
                    )}
                    {apiCfg.authType === "basic" && (
                      <>
                        <div className="field"><label>Username</label><input type="text" value={apiCfg.username} onChange={(e) => setApiCfg({ ...apiCfg, username: e.target.value })} /></div>
                        <div className="field"><label>Password</label><input type="text" value={apiCfg.password} placeholder="••••••••" onChange={(e) => setApiCfg({ ...apiCfg, password: e.target.value })} /></div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---------- STEP 1 — MAP ---------- */}
          {step === 1 && (
            <div className="step-panel">
              <div className="wizard-q">What do its columns mean?</div>
              <p className="wizard-help">We line up the source's columns with the <b>{titleize(stream)}</b> stream's {canonical.length} standard
                fields. Probe the file to read its real headers, then let auto-match do the heavy lifting — you just confirm.</p>

              {!probe ? (
                <div className="dropzone" style={{ cursor: "default" }}>
                  <div className="dz-ic"><Icon name="search" size={26} /></div>
                  <b>Read the source's columns</b>
                  <small>We'll peek at the headers and a few sample values — read-only.</small>
                  <div style={{ marginTop: 14 }}>
                    <button type="button" className="btn primary" onClick={() => doProbe()} disabled={probing}>
                      {probing ? <><span className="spinner" /> Looking inside…</> : <><Icon name="search" size={15} /> Probe the source</>}
                    </button>
                  </div>
                  {isEdit && schemaSaved && (
                    <small style={{ marginTop: 10 }}>A saved mapping already exists — probing is only needed to change it.</small>
                  )}
                </div>
              ) : (
                <>
                  {probe.sheets?.length > 1 && (
                    <div className="controls" style={{ marginBottom: 12 }}>
                      <div className="field"><label>Worksheet</label>
                        <select value={sheet} onChange={(e) => { setSheet(e.target.value); doProbe(e.target.value); }}>
                          {probe.sheets.map((s) => <option key={s}>{s}</option>)}
                        </select></div>
                    </div>
                  )}
                  <div className="map-progress">
                    <div className="pbar"><i style={{ width: `${canonical.length ? (mappedCount / canonical.length) * 100 : 0}%` }} /></div>
                    <b>{mappedCount}/{canonical.length} mapped</b>
                    <button type="button" className="btn soft sm" onClick={autoMatch}><Icon name="wand" size={14} /> Auto-match</button>
                  </div>
                  {!allMapped && <div className="error" style={{ marginTop: 0 }}><Icon name="alert" size={15} /> Map all {canonical.length} fields before saving (highlighted rows are missing).</div>}
                  <div className="map-table">
                    {canonical.map((c) => {
                      const mapped = !!mapping[c];
                      const samples = probe.columns.find((p) => p.name === mapping[c])?.samples?.join(" · ") ?? "";
                      return (
                        <div key={c} className={`map-row ${mapped ? "mapped" : "unmapped"}`}
                          style={justMatched && mapped ? { animation: "popin .4s var(--ease-back) both" } : undefined}>
                          <div className="map-canon">
                            <div><b>{titleize(c)}</b>
                              <small className="num">{c}{isDateLike(c) && " 📅"}</small></div>
                          </div>
                          <div className="arrow"><Icon name="arrowR" size={16} /></div>
                          <div>
                            <select value={mapping[c] ?? ""} onChange={(e) => setMapping({ ...mapping, [c]: e.target.value })}>
                              <option value="">— not mapped —</option>
                              {sourceCols.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            {isDateLike(c) && mapped && (
                              <input type="text" value={formats[c] ?? ""} placeholder="date format e.g. %d/%m/%Y (blank = ISO)"
                                onChange={(e) => setFormats({ ...formats, [c]: e.target.value })} style={{ marginTop: 6, width: "100%", minWidth: 0 }} />
                            )}
                          </div>
                          <div className="map-samples">{samples}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ---------- STEP 2 — TEST ---------- */}
          {step === 2 && (
            <div className="step-panel">
              <div className="wizard-q">Test it — a safe dry run</div>
              <p className="wizard-help">We push the source through the entire pipeline without saving or publishing anything.
                You'll see exactly what would happen, and why.</p>

              {!test && !testing && (
                <div className="dropzone" style={{ cursor: "default" }}>
                  <div className="dz-ic"><Icon name="flask" size={26} /></div>
                  <b>Run a dry-run test</b>
                  <small>Nothing is recorded or published — promise.</small>
                  <div style={{ marginTop: 14 }}>
                    <button type="button" className="btn primary lg" onClick={runTest} disabled={!(schemaSaved || isEdit)}>
                      <Icon name="play" size={15} /> Run test
                    </button>
                  </div>
                  {!(schemaSaved || isEdit) && <small style={{ marginTop: 10, color: "var(--warn)" }}>Save the column mapping first (step 2).</small>}
                </div>
              )}

              {testing && (
                <div className="card pad" style={{ textAlign: "center", padding: 28 }}>
                  <div className="steps" style={{ justifyContent: "center" }}>
                    {["Received", "Standardised", "Calculated", "Quality checks"].map((l, i) => {
                      const st = testStage > i ? "done" : testStage === i ? "pending" : "";
                      return <span key={l} style={{ display: "contents" }}>
                        <div className={`st ${st}`}><div className="b">{st === "done" ? <Icon name="check" size={10} /> : i + 1}</div>{l}</div>
                        {i < 3 && <div className={`conn ${testStage > i ? "done" : ""}`} />}
                      </span>;
                    })}
                  </div>
                  <p className="hint" style={{ marginTop: 16 }}><span className="spinner" style={{ marginRight: 8, color: "var(--primary)" }} /> Running the pipeline…</p>
                </div>
              )}

              {test && !test.ok && (
                <div className="error"><Icon name="alert" size={15} /> Test stopped: {test.error}</div>
              )}

              {test && test.ok && (
                <>
                  <div className={`test-verdict ${test.verdict === "clean" ? "clean" : "blocked"}`}>
                    <div className="tv-ic"><Icon name={test.verdict === "clean" ? "check" : "alert"} size={28} /></div>
                    <div className="tv-txt">
                      <b>{test.verdict === "clean" ? "This source would publish cleanly" : "This source would be blocked"}</b>
                      <span>{test.verdict === "clean" ? "All quality checks pass. You're ready to process it for real." : test.reasons.join(" · ")}</span>
                    </div>
                  </div>
                  <div className="test-stats">
                    <div className="test-stat"><div className="v">{fmtN(test.stagedRows)}</div><div className="l">Rows processed</div></div>
                    <div className="test-stat"><div className="v">{fmtRm(test.netSpend)}</div><div className="l">Headline value</div></div>
                    <div className="test-stat"><div className="v">{fmtN(test.entities)}</div><div className="l">Entities</div></div>
                  </div>
                  {test.unmappedColumns.length > 0 && (
                    <p className="hint"><Icon name="info" size={13} /> {test.unmappedColumns.length} source columns aren't mapped and will be ignored:{" "}
                      <span className="num">{test.unmappedColumns.slice(0, 8).join(", ")}{test.unmappedColumns.length > 8 ? "…" : ""}</span></p>
                  )}
                  <div className="eyebrow" style={{ marginTop: 16 }}>Sample of the final data this source would produce</div>
                  <div className="scroll-x" style={{ marginTop: 8 }}>
                    <table className="dense">
                      <thead><tr>{test.sampleColumns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                      <tbody>
                        {test.sampleRows.map((row, i) => (
                          <tr key={i}>{row.map((v, j) => <td key={j} className={v === null ? "null-cell" : ""}>{v ?? "∅ empty"}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* footer nav */}
        <div className="wizard-foot">
          {isEdit && step === 1 && (
            <button type="button" className="btn danger sm" onClick={removeSource}><Icon name="trash" size={14} /> Delete source</button>
          )}
          {step > 0 && (
            <button type="button" className="btn ghost" onClick={() => setStep(step - 1)}>
              <Icon name="arrowR" size={15} style={{ transform: "rotate(180deg)" }} /> Back
            </button>
          )}
          <div className="spacer" />
          <button type="button" className="btn ghost" onClick={onClose}>Close</button>
          {step === 0 && (
            <button type="button" className="btn primary" disabled={!canConnect || uploading} onClick={nextFromConnect}>
              Next: map columns <Icon name="arrowR" size={15} />
            </button>
          )}
          {step === 1 && (
            <button type="button" className="btn primary" disabled={!((probe && allMapped) || schemaSaved)} onClick={nextFromMap}>
              Next: test it <Icon name="arrowR" size={15} />
            </button>
          )}
          {step === 2 && (
            <button type="button" className="btn primary" disabled={!test || !test.ok} onClick={onClose}>
              <Icon name="check" size={15} /> Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
